/**
 * API client for rule42-cloud (cockpit-server).
 *
 * Automatically attaches the Bearer token from session storage.
 * Handles 401 by clearing the session and redirecting to login.
 *
 * @module apps/web/lib/api
 * @since W2.9
 */

import { ensureValidSession, clearSession, login, type AuthSession } from './auth.js';
import { getStoredActiveOrgId } from './active-org.js';

const BASE_URL = import.meta.env.VITE_API_URL ?? '';

class ApiError extends Error {
  constructor(
    public readonly status: number,
    message: string,
    public readonly body?: unknown,
  ) {
    super(message);
    this.name = 'ApiError';
  }
}

function getApiErrorMessage(status: number, body: unknown): string {
  const apiBody = body as { message?: unknown; error?: unknown } | undefined;
  const message = typeof apiBody?.message === 'string' ? apiBody.message : undefined;

  if (status === 401) {
    return message ?? 'Session expired or authentication failed. Sign in again.';
  }

  if (status === 403) {
    return message ?? 'You do not have permission to perform this action.';
  }

  return message ?? `API error ${status}`;
}

async function request<T>(path: string, init?: RequestInit): Promise<T> {
  const session = await ensureValidSession();
  const activeOrgId = getStoredActiveOrgId(session?.claims.sub);
  const isFormData = typeof FormData !== 'undefined' && init?.body instanceof FormData;
  const buildHeaders = (authSession: AuthSession | null): Record<string, string> => {
    const headers: Record<string, string> = {
      ...(authSession?.accessToken ? { Authorization: `Bearer ${authSession.accessToken}` } : {}),
      ...(activeOrgId ? { 'X-Rule42-Active-Org-Id': activeOrgId } : {}),
      ...(init?.headers as Record<string, string> | undefined),
    };

    if (!isFormData && !headers['Content-Type']) {
      headers['Content-Type'] = 'application/json';
    }

    return headers;
  };

  let res = await fetch(`${BASE_URL}${path}`, { ...init, headers: buildHeaders(session) });

  if (res.status === 401) {
    const refreshedSession = await ensureValidSession(true, 0);
    if (refreshedSession?.accessToken && refreshedSession.accessToken !== session?.accessToken) {
      res = await fetch(`${BASE_URL}${path}`, { ...init, headers: buildHeaders(refreshedSession) });
    }

    if (res.status === 401) {
      let body: unknown;
      try { body = await res.json(); } catch { /* ignore */ }
      clearSession();
      await login(window.location.pathname + window.location.search);
      // login() redirects — this line is unreachable but satisfies TypeScript
      throw new ApiError(401, getApiErrorMessage(401, body), body);
    }
  }

  if (!res.ok) {
    let body: unknown;
    try { body = await res.json(); } catch { /* ignore */ }
    throw new ApiError(res.status, getApiErrorMessage(res.status, body), body);
  }

  if (res.status === 204) return undefined as T;
  return res.json() as Promise<T>;
}

function serializeRequestBody(body: unknown): BodyInit | undefined {
  if (body == null) {
    return undefined;
  }

  if (typeof FormData !== 'undefined' && body instanceof FormData) {
    return body;
  }

  return JSON.stringify(body);
}

export const api = {
  get:    <T>(path: string)                       => request<T>(path),
  post:   <T>(path: string, body?: unknown)        => request<T>(path, { method: 'POST',  body: serializeRequestBody(body) }),
  put:    <T>(path: string, body?: unknown)        => request<T>(path, { method: 'PUT',   body: serializeRequestBody(body) }),
  patch:  <T>(path: string, body?: unknown)        => request<T>(path, { method: 'PATCH', body: serializeRequestBody(body) }),
  delete: <T>(path: string)                        => request<T>(path, { method: 'DELETE' }),
};

export { ApiError };
